/**
 * Shared utilities for report dashboards
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { getNestedValue } from "@/lib/object-utils";
/**
 * Computes objective values for each data row
 */
export function computeObjectiveValues(data, objectives) {
    return __awaiter(this, void 0, void 0, function* () {
        return Promise.all(data.map((row) => __awaiter(this, void 0, void 0, function* () {
            const computedRow = Object.assign({}, row);
            for (const objective of objectives) {
                computedRow[objective.id] = yield objective.computeValues(row);
            }
            return computedRow;
        })));
    });
}
/**
 * Computes additional columns based on already computed rows (after objectives)
 */
export function computeColumns(data, computedColumns) {
    if (!computedColumns || computedColumns.length === 0) {
        return data;
    }
    return data.map((row) => {
        const rowWithComputedColumns = Object.assign({}, row);
        computedColumns.forEach((column) => {
            rowWithComputedColumns[column.key] = column.compute(row);
        });
        return rowWithComputedColumns;
    });
}
/**
 * Filters data based on active filters
 */
export function filterData(data, activeFilters) {
    if (!data) {
        return [];
    }
    let filtered = data;
    Object.entries(activeFilters).forEach(([filterKey, filterValues]) => {
        if (filterValues && filterValues.length > 0) {
            filtered = filtered.filter((item) => {
                const itemValue = getNestedValue(item, filterKey);
                return filterValues.includes(String(itemValue));
            });
        }
    });
    return filtered;
}
/**
 * Sorts data based on sort configurations
 */
export function sortData(data, sorts) {
    if (!data || sorts.length === 0) {
        return data || [];
    }
    return [...data].sort((a, b) => {
        for (const sortConfig of sorts) {
            const { field, direction } = sortConfig;
            const aValue = getNestedValue(a, field);
            const bValue = getNestedValue(b, field);
            // Handle null/undefined
            if (aValue == null && bValue == null)
                continue;
            if (aValue == null)
                return 1;
            if (bValue == null)
                return -1;
            // Compare values
            let comparison = 0;
            if (typeof aValue === "string" && typeof bValue === "string") {
                comparison = aValue.localeCompare(bValue);
            }
            else if (typeof aValue === "number" && typeof bValue === "number") {
                comparison = aValue - bValue;
            }
            else {
                comparison = String(aValue).localeCompare(String(bValue));
            }
            if (comparison !== 0) {
                return direction === "asc" ? comparison : -comparison;
            }
        }
        return 0;
    });
}
/**
 * Extracts unique filter options from data
 */
export function extractFilterOptions(data, filterKeys) {
    const options = {};
    filterKeys.forEach((key) => {
        const uniqueValues = [...new Set(data.map((item) => getNestedValue(item, key)))]
            .filter(Boolean)
            .map((value) => ({ label: String(value), value: String(value) }));
        options[key] = uniqueValues;
    });
    return options;
}
/**
 * Extracts chained filter options, applying every other active filter except the current one.
 */
export function extractChainedFilterOptions(data, filterKeys, activeFilters) {
    const options = {};
    filterKeys.forEach((key) => {
        var _a;
        const rowsForFilter = filterData(data, Object.assign(Object.assign({}, activeFilters), { [key]: [] }));
        options[key] = (_a = extractFilterOptions(rowsForFilter, [key])[key]) !== null && _a !== void 0 ? _a : [];
    });
    return options;
}
/**
 * Builds field list for table (static fields + objective field groups + computed columns)
 */
export function buildTableFields(fieldRepository, // FieldRepository
staticFieldKeys, objectives, computedColumnKeys) {
    const fields = [
        ...staticFieldKeys.map((key) => fieldRepository.get(key)),
        ...objectives.map((obj) => obj.getFieldGroup(fieldRepository)),
    ];
    // Add computed column fields at the end
    if (computedColumnKeys && computedColumnKeys.length > 0) {
        fields.push(...computedColumnKeys.map((key) => fieldRepository.get(key)));
    }
    return fields;
}
/**
 * Checks if any filters are active
 */
export function hasActiveFilters(filters) {
    return Object.values(filters).some((values) => values.length > 0);
}
